/**
 * Editor-side companion to `resolveBusinessCtaText`.
 *
 * The precedence itself is correct and deliberate: a label set once in Settings should win
 * over whatever a page happens to have saved, so changing it in one place updates every
 * page. What was wrong is that the editor still offered an ordinary text box for the field,
 * so a pro could type into it, save, and see nothing change — with nothing on screen
 * explaining why.
 *
 * Components call this from `resolveFields` to swap the affected field for a read-only note
 * pointing at the setting that is actually in charge.
 */

interface BusinessCtaMetadata {
  business?: { ctaPrimaryLabel?: string | null };
}

/** The Settings label, when the business has explicitly set one. */
const getBusinessCtaLabel = (metadata: unknown): string | null => {
    const label = (metadata as BusinessCtaMetadata | undefined)?.business?.ctaPrimaryLabel,
      trimmed = typeof label === "string" ? label.trim() : "";
    if (trimmed.length > 0) {
      return trimmed;
    }
    return null;
  },

  /**
   * Replace a CTA-text field with a disabled one that names the overriding value, or return it
   * untouched when Settings has nothing to say.
   */
  withBusinessCtaOverride = <TFields extends Record<string, unknown>>(
    fields: TFields,
    key: keyof TFields & string,
    metadata: unknown,
  ): TFields => {
    const businessLabel = getBusinessCtaLabel(metadata),
      field = fields[key] as { label?: string } | undefined;

    if (!businessLabel || !field) {
      return fields;
    }

    return {
      ...fields,
      [key]: {
        ...field,
        label: `${field.label || "CTA Button Text"} — set in Settings`,
        metadata: {
          ...(field as { metadata?: Record<string, unknown> }).metadata,
          description: `Every page uses "${businessLabel}" from your business settings. Change it there to change it everywhere.`,
        },
        readOnly: true,
        type: "text",
      },
    };
  };

export { getBusinessCtaLabel, withBusinessCtaOverride };
